import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { Panel } from "@/components/rights/RightsBadges";
import { ApprovalBadge } from "@/components/platform/Badges";
import { ApprovalPanel } from "@/components/platform/ApprovalPanel";

const AddRow = ({ fields, onAdd, testId }) => {
  const [f, setF] = useState(Object.fromEntries(fields.map(([k]) => [k, ""])));
  const submit = async () => { if (await onAdd(f)) setF(Object.fromEntries(fields.map(([k]) => [k, ""]))); };
  return (
    <div className="flex gap-2 flex-wrap mt-3" data-testid={testId}>
      {fields.map(([k, ph]) => (
        <Input key={k} value={f[k]} placeholder={ph} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))}
               className="h-8 text-xs flex-1 min-w-[120px]" data-testid={`${testId}-${k}`} />
      ))}
      <Button size="sm" className="h-8" disabled={!f[fields[0][0]].trim()} onClick={submit} data-testid={`${testId}-submit`}><Plus className="w-3.5 h-3.5" /></Button>
    </div>
  );
};

const SubprocessorApproval = ({ s, canEdit, currentUser, onRequest, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [approval, setApproval] = useState(null);
  const label = s.approved ? "Approved" : (s.approval_status || "Not requested");
  const toggle = async () => {
    if (!open && s.approval_id) {
      try {
        const { data } = await api.get(`/platform/approvals?object_type=vendor_subprocessor&object_id=${s.id}`);
        setApproval(data.approvals?.[0] || null);
      } catch (e) { toast.error(apiError(e, "Could not load approval")); return; }
    }
    setOpen((x) => !x);
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        <ApprovalBadge status={label} testId={`subprocessor-status-${s.id}`} />
        {s.approval_id ? (
          <button className="text-slate-400 hover:text-[#003366]" onClick={toggle} data-testid={`subprocessor-approval-toggle-${s.id}`}>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        ) : canEdit ? (
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={onRequest} data-testid={`subprocessor-request-approval-${s.id}`}>Request approval</Button>
        ) : null}
        {s.approval_status === "Rejected" && canEdit && (
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={onRequest} data-testid={`subprocessor-rerequest-approval-${s.id}`}>Request again</Button>
        )}
      </div>
      {open && approval && (
        <div className="w-full mt-1">
          <ApprovalPanel approval={approval} currentUser={currentUser}
                         onChanged={(a) => { setApproval(a); onChanged(); }} />
        </div>
      )}
    </div>
  );
};

export const ThirdPartiesPanel = ({ v, canEdit, currentUser, onChanged }) => {
  const call = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); return true; } catch (e) { toast.error(apiError(e, "Action failed")); return false; }
  };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <Panel eyebrow="Fourth parties" title={`Sub-processors (${v.subprocessors.length})`} testId="subprocessors-panel">
        <ul className="divide-y divide-slate-100">
          {v.subprocessors.length === 0 && <li className="text-xs text-slate-500 py-2">No sub-processors recorded.</li>}
          {v.subprocessors.map((s) => (
            <li key={s.id} className="py-2.5 flex items-start gap-3 text-xs" data-testid={`subprocessor-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#003366]">{s.name}</div>
                <div className="text-[11px] text-slate-500">{[s.service, s.country, s.data_shared].filter(Boolean).join(" · ")}</div>
              </div>
              <SubprocessorApproval s={s} canEdit={canEdit} currentUser={currentUser} onChanged={onChanged}
                                    onRequest={() => call(() => api.post(`/vendors/${v.id}/subprocessors/${s.id}/request-approval`, {}), "Approval requested")} />
              {canEdit && <button className="text-slate-300 hover:text-red-600 mt-0.5" data-testid={`subprocessor-remove-${s.id}`}
                                  onClick={() => call(() => api.delete(`/vendors/${v.id}/subprocessors/${s.id}`), "Sub-processor removed")}><Trash2 className="w-3.5 h-3.5" /></button>}
            </li>
          ))}
        </ul>
        {canEdit && <AddRow testId="subprocessor-add" fields={[["name", "Name"], ["service", "Service"], ["country", "Country"], ["data_shared", "Data shared"]]}
                            onAdd={(f) => call(() => api.post(`/vendors/${v.id}/subprocessors`, { ...f, country: f.country || "India" }), "Sub-processor added")} />}
      </Panel>
      <Panel eyebrow="People" title={`Vendor contacts (${v.contacts.length})`} testId="contacts-panel">
        <ul className="divide-y divide-slate-100">
          {v.contacts.length === 0 && <li className="text-xs text-slate-500 py-2">No contacts recorded.</li>}
          {v.contacts.map((c) => (
            <li key={c.id} className="py-2.5 flex items-center gap-3 text-xs" data-testid={`contact-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#003366]">{c.name}{c.is_primary && <span className="ml-1.5 text-[10px] text-[#008080]">primary</span>}</div>
                <div className="text-[11px] text-slate-500">{[c.role, c.email, c.phone].filter(Boolean).join(" · ")}</div>
              </div>
              {canEdit && <button className="text-slate-300 hover:text-red-600" data-testid={`contact-remove-${c.id}`}
                                  onClick={() => call(() => api.delete(`/vendors/${v.id}/contacts/${c.id}`), "Contact removed")}><Trash2 className="w-3.5 h-3.5" /></button>}
            </li>
          ))}
        </ul>
        {canEdit && <AddRow testId="contact-add" fields={[["name", "Role"], ["role", "Role"], ["email", "Email"], ["phone", "Phone"]]}
                            onAdd={(f) => call(() => api.post(`/vendors/${v.id}/contacts`, f), "Contact added")} />}
      </Panel>
    </div>
  );
};
